import { forwardRef } from "react";
import { Platform, Text, TextInput, View, type KeyboardTypeOptions, type ReturnKeyTypeOptions } from "react-native";

import { FONTS } from "../../lib/brand";

export const Input = forwardRef<TextInput, {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  error?: string;
  helper?: string;
  variant?: "light" | "dark";
  keyboardType?: KeyboardTypeOptions;
  secureTextEntry?: boolean;
  multiline?: boolean;
  valid?: boolean;
  /** Amber border (e.g. unrecognized email domain) without treating as submit error. */
  warn?: boolean;
  editable?: boolean;
  onSubmitEditing?: () => void;
  returnKeyType?: ReturnKeyTypeOptions;
  /** Renders a small red asterisk after the label. Pure visual hint; does not enforce anything. */
  required?: boolean;
  /** Fires when the input loses focus — used by callers that want to defer
   *  validation messaging until the user moves on (e.g. confirm-password). */
  onBlur?: () => void;
  /**
   * Browser autofill hint. Pass values like "email", "current-password",
   * "new-password", "name", "tel". For sign-in pass "current-password" so saved
   * passwords get offered; for signup leave empty (the secureTextEntry web
   * shim defaults to "new-password" to prevent partial-password capture).
   */
  autoComplete?: string;
  /** iOS-only equivalent of autoComplete. RN passes through to UITextField. */
  textContentType?: string;
  /** Focus the field on mount. RN-web's autoFocus can be flaky — callers that
   *  need a guaranteed focus should also call `.focus()` on the forwarded ref. */
  autoFocus?: boolean;
  maxLength?: number;
}>(function Input(
  {
    label,
    value,
    onChangeText,
    placeholder,
    error,
    helper,
    variant = "light",
    keyboardType,
    secureTextEntry,
    multiline,
    valid,
    warn,
    editable = true,
    onSubmitEditing,
    returnKeyType,
    required,
    autoComplete,
    textContentType,
    onBlur,
    autoFocus,
    maxLength,
  },
  ref,
) {
  const base = variant === "dark" ? "bg-white/5 border-white/10 text-white" : "bg-white border-sand-dark text-text";
  const state = error
    ? variant === "dark"
      ? "border-error-bright bg-error/10"
      : "border-error bg-red-50"
    : valid
      ? "border-success"
      : warn
        ? "border-amber"
        : "";
  const webSecurePassword =
    Platform.OS === "web" && secureTextEntry
      ? {
          onChange: (e: any) => {
            const next = (e?.nativeEvent?.text ?? e?.target?.value ?? "") as string;
            if (next !== value) onChangeText(next);
          },
          // Caller-provided autoComplete wins (e.g. login passes
          // "current-password"). Default to "new-password" only when caller
          // didn't specify, so signup forms still suppress partial-password
          // capture by browser password managers.
          autoComplete: (autoComplete ?? "new-password") as any,
        }
      : null;

  return (
    <View className="w-full">
      <Text className={`mb-1 text-[10px] font-bold uppercase tracking-[0.16em] ${variant === "dark" ? "text-white/60" : "text-text/65"}`}>
        {label}
        {required ? <Text className="text-error-bright"> *</Text> : null}
      </Text>
      <TextInput
        ref={ref}
        value={value}
        onChangeText={onChangeText}
        {...(webSecurePassword ?? {})}
        autoComplete={
          // Web shim above already injected autoComplete for secureTextEntry;
          // skip here to avoid double-binding. For everything else pass through
          // whatever the caller provided.
          webSecurePassword ? undefined : (autoComplete as any)
        }
        textContentType={textContentType as any}
        placeholder={placeholder}
        placeholderTextColor={variant === "dark" ? "rgba(255,255,255,0.40)" : "rgba(26,26,24,0.30)"}
        keyboardType={keyboardType}
        secureTextEntry={secureTextEntry}
        multiline={multiline}
        editable={editable}
        autoFocus={autoFocus}
        maxLength={maxLength}
        textAlignVertical={multiline ? "top" : "center"}
        style={{ fontFamily: FONTS.body }}
        className={`rounded-btn border px-3 py-2.5 text-[14px] ${base} ${state} ${multiline ? "min-h-[110px]" : ""} ${!editable ? "opacity-60" : ""}`}
        onSubmitEditing={onSubmitEditing}
        returnKeyType={returnKeyType}
        onBlur={onBlur}
      />
      {helper && !error ? <Text className={`mt-1 text-[12px] ${variant === "dark" ? "text-white/50" : "text-text/55"}`}>{helper}</Text> : null}
      {error ? <Text className="mt-1 text-[12px] text-error-bright">{error}</Text> : null}
    </View>
  );
});
